import React from 'react';

import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import ElectricalServicesOutlinedIcon from '@mui/icons-material/ElectricalServicesOutlined';
import RouterOutlinedIcon from '@mui/icons-material/RouterOutlined';
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';

type ServicesTechnicalProps = {
  onBack: () => void;
};

export default function ServicesTechnical({ onBack }: ServicesTechnicalProps): React.ReactElement {
  return (
    <section className="bg-white">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-20">
        <div className="mx-auto max-w-5xl">
          <button
            onClick={onBack}
            className="mb-6 flex items-center gap-2 text-sky-600 hover:text-sky-700 transition-colors"
          >
            <ArrowBackOutlinedIcon fontSize="small" />
            <span className="text-sm font-semibold">Back to Services</span>
          </button>
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-sky-600 text-white text-sm font-semibold mb-4">
              Service Four
            </span>
            <h2 className="text-3xl font-semibold leading-tight text-slate-900 sm:text-4xl">
              Technical Services, ICT & Security
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg max-w-3xl mx-auto">
              We protect your assets and optimize your facility's digital infrastructure through integrated technical
              services.
            </p>
          </div>

          {/* Comprehensive Support Services */}
          <div className="mb-10">
            <h3 className="text-2xl font-semibold text-slate-900 mb-6 text-center">
              Comprehensive Support Services
            </h3>
            
            {/* Advanced Security Engineering */}
            <div className="mb-8">
              <h4 className="text-xl font-semibold text-slate-900 mb-4 text-center">
                Advanced Security Engineering
              </h4>
              <div className="grid gap-6 lg:grid-cols-3">
                <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                      <LockOutlinedIcon fontSize="medium" />
                    </div>
                    <h5 className="text-lg font-semibold text-slate-900">Access Control</h5>
                  </div>
                  <p className="text-sm leading-relaxed text-slate-600">
                    Biometric systems and high-security doors for restricted areas.
                  </p>
                </div>

                <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                      <VideocamOutlinedIcon fontSize="medium" />
                    </div>
                    <h5 className="text-lg font-semibold text-slate-900">Surveillance</h5>
                  </div>
                  <p className="text-sm leading-relaxed text-slate-600">
                    Integrated CCTV networks with remote monitoring capabilities.
                  </p>
                </div>

                <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                      <LocalFireDepartmentOutlinedIcon fontSize="medium" />
                    </div>
                    <h5 className="text-lg font-semibold text-slate-900">Safety</h5>
                  </div>
                  <p className="text-sm leading-relaxed text-slate-600">
                    Fire alarm panel design and installation.
                  </p>
                </div>
              </div>
            </div>

            {/* Electrical & IT Infrastructure */}
            <div className="mb-8">
              <h4 className="text-xl font-semibold text-slate-900 mb-4 text-center">
                Electrical & IT Infrastructure
              </h4>
              <div className="grid gap-6 lg:grid-cols-2">
                <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                      <ElectricalServicesOutlinedIcon fontSize="medium" />
                    </div>
                    <h5 className="text-lg font-semibold text-slate-900">Electrical Distribution</h5>
                  </div>
                  <p className="text-sm leading-relaxed text-slate-600">
                    Full-service electrical distribution for commercial and industrial buildings.
                  </p>
                </div>

                <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                      <RouterOutlinedIcon fontSize="medium" />
                    </div>
                    <h5 className="text-lg font-semibold text-slate-900">IT Networks</h5>
                  </div>
                  <p className="text-sm leading-relaxed text-slate-600">
                    IT network design, supply, and ongoing hardware maintenance.
                  </p>
                </div>
              </div>
            </div>

            {/* Plant & Process Optimization */}
            <div className="mb-8">
              <h4 className="text-xl font-semibold text-slate-900 mb-4 text-center">
                Plant & Process Optimization
              </h4>
              <div className="grid gap-6 lg:grid-cols-3">
                <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                      <FactoryOutlinedIcon fontSize="medium" />
                    </div>
                    <h5 className="text-lg font-semibold text-slate-900">Layout Design</h5>
                  </div>
                  <p className="text-sm leading-relaxed text-slate-600">
                    Layout design for new facilities to maximize throughput and safety.
                  </p>
                </div>

                <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                      <AssessmentOutlinedIcon fontSize="medium" />
                    </div>
                    <h5 className="text-lg font-semibold text-slate-900">Energy Audits</h5>
                  </div>
                  <p className="text-sm leading-relaxed text-slate-600">
                    Comprehensive analysis of power consumption to identify wastage and save costs.
                  </p>
                </div>

                <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                      <BuildCircleOutlinedIcon fontSize="medium" />
                    </div>
                    <h5 className="text-lg font-semibold text-slate-900">Shutdown Maintenance</h5>
                  </div>
                  <p className="text-sm leading-relaxed text-slate-600">
                    Turnkey management of scheduled industrial shutdowns.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Industrial Training & Capacity Building */}
          <div className="rounded-2xl bg-sky-600 p-8 lg:p-10 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <SchoolOutlinedIcon className="text-white" fontSize="large" />
              <h4 className="text-2xl font-semibold text-white">Industrial Training & Capacity Building</h4>
            </div>
            <p className="text-base leading-relaxed text-white/90 max-w-2xl mx-auto">
              We offer corporate training programs in Industrial Automation, Embedded Systems, and Building Management
              Systems (BMS) to ensure your staff can operate modern equipment effectively.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}


